/**
 * The text of one language of the Abby welcome: one text box per paragraph
 * (spec 2026-10-23 §5 Tab 2; R18).
 *
 * A paragraph is one block of text — the server refuses a line break — so:
 *   - Enter (without Shift) splits the paragraph at the caret and moves focus
 *     to the new paragraph; Shift+Enter adds nothing and says why;
 *   - a paste with line breaks becomes paragraphs (empty lines dropped);
 *   - past 12 paragraphs, every edit is refused in plain words.
 * The edits are pure functions (lib/paragraph-edits.ts); the handlers, the
 * refusal and the focus moves are in ../useParagraphEditor.ts (tested through
 * react-test-renderer); this file only binds them. A read-only editor does
 * nothing on Enter, a paste, typing or a button.
 *
 * Accessibility: every control is a real button with a name that includes the
 * paragraph number ("Move paragraph 3 up"); reorder never needs a drag. A
 * refusal is an alert; a move, an add or a remove is said in a polite live
 * region that exists from the first render. The counter and the problem of a
 * paragraph are tied to its text box with aria-describedby.
 */
import { ArrowDown, ArrowUp, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WELCOME_MAX_PARAGRAPHS, WELCOME_PARAGRAPH_MAX_CHARS, type ExperienceLocale } from '../lib/contract';
import { LANGUAGE_NAME } from '../lib/intro-state';
import { countedLength, paragraphProblem } from '../lib/welcome-text';
import { useParagraphEditor } from '../useParagraphEditor';

interface Props {
  locale: ExperienceLocale;
  paragraphs: string[];
  /** The text is the built-in text (trimmed). */
  builtIn: boolean;
  /** Read-only: the boxes can be read, nothing can change. */
  disabled: boolean;
  onChange: (paragraphs: string[]) => void;
  /** "Reset to the built-in text"; the parent asks for confirmation. */
  onReset: () => void;
  /** Start of the element ids. */
  idPrefix: string;
}

const ICON_BUTTON =
  'inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40';

export function ParagraphListEditor({ locale, paragraphs, builtIn, disabled, onChange, onReset, idPrefix }: Props) {
  const name = LANGUAGE_NAME[locale];
  const ids = `${idPrefix}-${locale}`;
  const ed = useParagraphEditor({ paragraphs, disabled, onChange });

  return (
    <section aria-labelledby={`${ids}-heading`} className="space-y-3 rounded-lg border bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="space-y-1">
          <h4 id={`${ids}-heading`} className="flex items-center gap-2 text-base font-semibold">
            {name} text
            <span
              id={`${ids}-source`}
              className={cn(
                'rounded-full border px-2 py-0.5 text-xs font-medium',
                builtIn
                  ? 'border-border bg-muted text-muted-foreground'
                  : 'border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200',
              )}
            >
              {builtIn ? 'Built-in text' : 'Your text'}
            </span>
          </h4>
          {locale === 'es' && (
            <p id={`${ids}-hint`} className="text-xs font-medium">
              Spanish: Peninsular, informal tú
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onReset}
          disabled={disabled || builtIn}
          aria-describedby={`${ids}-source`}
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
          Reset to the built-in text
        </button>
      </div>

      <p id={`${ids}-help`} className="text-xs text-muted-foreground">
        One box for each paragraph, {paragraphs.length} of {WELCOME_MAX_PARAGRAPHS}. Press Enter to start a new paragraph. A
        paragraph can have up to {WELCOME_PARAGRAPH_MAX_CHARS} characters.
      </p>

      <p className="sr-only" aria-live="polite">
        {ed.said}
      </p>
      {ed.refusal && (
        <p role="alert" className="rounded-md border border-red-300 bg-red-50 p-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          {ed.refusal}
        </p>
      )}

      <ol className="space-y-3">
        {paragraphs.map((text, i) => {
          const n = i + 1;
          const boxId = `${ids}-p${n}`;
          const length = countedLength(text);
          const tooLong = length > WELCOME_PARAGRAPH_MAX_CHARS;
          const problem = paragraphProblem(text, i);
          const describedBy = [`${boxId}-count`, problem ? `${boxId}-problem` : null, `${ids}-help`, locale === 'es' ? `${ids}-hint` : null]
            .filter(Boolean)
            .join(' ');
          return (
            <li key={i} className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <label htmlFor={boxId} className="text-sm font-medium">
                  Paragraph {n}
                </label>
                <div className="flex gap-1">
                  <button
                    ref={ed.upRef(i)}
                    type="button"
                    aria-label={`Move paragraph ${n} up`}
                    title="Move up"
                    disabled={disabled || i === 0}
                    onClick={() => ed.moveUp(i)}
                    className={ICON_BUTTON}
                  >
                    <ArrowUp className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button
                    ref={ed.downRef(i)}
                    type="button"
                    aria-label={`Move paragraph ${n} down`}
                    title="Move down"
                    disabled={disabled || i === paragraphs.length - 1}
                    onClick={() => ed.moveDown(i)}
                    className={ICON_BUTTON}
                  >
                    <ArrowDown className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Add a paragraph after paragraph ${n}`}
                    title="Add a paragraph below"
                    disabled={disabled}
                    onClick={() => ed.addBelow(i)}
                    className={ICON_BUTTON}
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove paragraph ${n}`}
                    title="Remove"
                    disabled={disabled}
                    onClick={() => ed.remove(i)}
                    className={ICON_BUTTON}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <textarea
                ref={ed.textRef(i)}
                id={boxId}
                value={text}
                rows={3}
                readOnly={disabled}
                aria-invalid={problem ? true : undefined}
                aria-describedby={describedBy}
                onChange={(e) => ed.onText(i, e.target.value)}
                onKeyDown={ed.onKeyDown(i)}
                onPaste={ed.onPaste(i)}
                className={cn(
                  'block w-full rounded-md border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary',
                  problem && 'border-red-400 dark:border-red-700',
                  disabled && 'opacity-70',
                )}
              />
              <div className="flex flex-wrap justify-between gap-2 text-xs">
                <span id={problem ? `${boxId}-problem` : undefined} className="text-red-700 dark:text-red-400">
                  {problem}
                </span>
                <span id={`${boxId}-count`} className={tooLong ? 'font-medium text-red-700 dark:text-red-400' : 'text-muted-foreground'}>
                  {length} / {WELCOME_PARAGRAPH_MAX_CHARS} characters{tooLong ? ' — too long' : ''}
                </span>
              </div>
            </li>
          );
        })}
      </ol>

      <button
        type="button"
        onClick={ed.addAtEnd}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Plus className="h-3.5 w-3.5" aria-hidden="true" />
        Add a paragraph at the end
      </button>
    </section>
  );
}
